import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { IonContent, IonPage } from '@ionic/react';
import Header from '../../components/Header';
import ArrivalInput from '../../components/SearchBar/ArrivalInput';
import { type DepartureAndArrival, type AutocompleteResponse } from '../../types/Autocomplete';
import DepartureInput from '../../components/SearchBar/DepartureInput';
import { useUserLocation } from '../../hooks/UserLocationHook';
import DetailModal from '../../components/DetailModal/DetailModal';
import GpsFloatingActions from '../../components/FloatingActions/GpsFloatingActions';
import { type Route } from '../../types/Forms';
import { type UnifiedItineraryDTO, type UnifiedLegDTO } from '../../types/Itinerary';
import SettingsFloatingActions from '../../components/FloatingActions/SettingsFloatingActions';
import Modal from '../../components/Modal/Modal';
import InputSliders from '../../components/Modal/InputSliders';
import TripList from '../../components/TripsPlanner/TripList';
import { useHistory, useLocation } from 'react-router-dom';
import { MapComponent } from '../../components/MapLibre/MapComponent';
import { VehicleManager } from '../../managers/VehicleManager';
import { useMain } from '../../store/StoredContext';
import { useMapItinerary } from '../../hooks/MapItineraryHook';
import { useCarItinerary } from '../../hooks/CarItineraryHook';
import { useLocationContext } from '../../store/LocationContext';
import NavigateFloatingActions from '../../components/FloatingActions/NavigateFloatingActions';
import { useEphemeral } from '../../store/EphemeralContext';

const EXPIRATION_TIME = 24 * 60 * 60 * 1000; // 24 hours in milliseconds
const vehicleManager = new VehicleManager();

const Home = () => {
  // Arrival fetshed autocomplete values
  const [autocompleteValues, setAutocompleteValues] = useState<AutocompleteResponse[]>([]);
  // Departure fetshed autocomplete values
  const [autocompleteDepartureValues, setAutocompleteDepartureValues] = useState<AutocompleteResponse[]>([]);
  // Selected address from arrival search input
  const [arrivalSelectedAddress, setArrivalSelectedAddress] = useState<AutocompleteResponse | null>(null);
  // Arrival Input value
  const [arrivalInput, setArrivalInput] = useState<string>('');
  // Selected address from departure search input
  const [departureSelectedAddress, setDepartureSelectedAddress] = useState<AutocompleteResponse | null>({
    location: {
      lat: -1,
      lon: -1
    },
    address: 'Ma position',
    source: 'mylocation'
  });
  // Departure Input value
  const [departureInput, setDepartureInput] = useState<string>('');
  // Swipeable container states
  const [visible, setVisible] = useState<boolean>(false);
  // calendar states
  // date & time data
  const [date, setDate] = useState<number>(Date.now);
  // Check if edited date or not
  const [isEditedDate, setIsEditedDate] = useState(false);
  // Arrival search input position
  const [position, setPosition] = useState<string>('51');
  const [isMerge, setIsMerge] = useState<boolean>(false);
  // Detail modal arrow top state
  const [showArrowTop, setShowArrowTop] = useState<boolean>(false);
  // Trip List container height
  const [tripListHeight, setTripListHeight] = useState<number>(50);
  // Selected trips to see in map
  const [selectedTrip, setSelectedTrip] = useState<UnifiedItineraryDTO | null>(null);
  const [selectedLeg, setSelectedLeg] = useState<UnifiedLegDTO | null>(null);
  // Animated transports
  const [animated, setAnimated] = useState(true);
  // I take my car choice state
  const [ITakeMyCar, setITakeMyCar] = useState<boolean>(false);
  const [ITakeMyCarData, setITakeMyCarData] = useState<UnifiedItineraryDTO | null>(null);
  const [selectedItinerary, setSelectedItinerary] = useState<string>('');
  const [itineraries, setItineraries] = useState<Route[]>([]);
  const [goItineraries, setGoItineraries] = useState<Route[]>([]);
  const [backupReturnItineraries, setBackupReturnItineraries] = useState<Route[]>([]);
  // Trip list Journeys state
  const [journeys, setJourneys] = useState<UnifiedItineraryDTO[]>([]);
  // User selected trip
  const [selectedInputs, setSelectedInputs] = useState<DepartureAndArrival>({
    departure: {
      lat: -1,
      lon: -1
    },
    arrival: {
      lat: 0,
      lon: 0
    }
  });

  const lastVisible = useRef(visible);

  // Fonction pour parser ou mettre à jour les query params
  const location = useLocation();
  const history = useHistory(); // ou useNavigate() si tu es en v6
  const queryParams = useMemo(() => {
    return new URLSearchParams(location.search);
  }, [location.search]);

  const {
    state: { departure: sourceInRedux },
    setDeparture,
    removeExpiredRoutes
  } = useMain();
  const {
    state: { map, trackingMode },
    switchTrackingMode
  } = useEphemeral();
  const { userLocation } = useLocationContext();

  const DepartureInputRef = useRef<HTMLInputElement>(null);

  const [, setResetMap] = useState(false);

  // Function to handle reset
  const handleResetMap = () => {
    setSelectedLeg(null); // Clear the selected leg
    setResetMap(true); // Trigger reset
  };

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isBikeFactorValuesChanged, setIsBikeFactorValuesChanged] = useState(false);

  const toggleModal = useCallback(() => {
    setIsModalOpen((prev) => !prev);
  }, []);

  /** Animate transports */
  // useTransportAnimation(animated, mapRef.current);
  /** Update user position */
  useUserLocation();

  /** Dessine les itinéraires sur la carte */
  useMapItinerary({ itinerary: selectedTrip, tripListHeight });

  useCarItinerary({ itineraries, selectedItinerary, setSelectedItinerary, tripListHeight });

  // Au montage, on lit l'URL et on alimente Redux
  useEffect(() => {
    const departure = queryParams.get('s');
    const departureLon = queryParams.get('slo');
    const departureLat = queryParams.get('sla');
    if (departureLat !== null && departureLon !== null) {
      // On retransforme `sourceParam` en objet AutocompleteResponse
      setDeparture({
        location: {
          lat: parseFloat(departureLat),
          lon: parseFloat(departureLon)
        },
        address: departure ?? `${departureLat},${departureLon}`,
        source: 'web'
      });
    }
  }, [queryParams, setDeparture]);

  // À chaque fois que `source` change dans Redux, on modifie l'URL
  useEffect(() => {
    if (sourceInRedux !== null) {
      queryParams.set('s', sourceInRedux.address);
      queryParams.set('slo', `${sourceInRedux.location.lon}`);
      queryParams.set('sla', `${sourceInRedux.location.lat}`);
    } else {
      queryParams.delete('s');
      queryParams.delete('slo');
      queryParams.delete('sla');
    }
    const newSearch = `?${queryParams.toString()}`;
    // On push la nouvelle URL (sans recharger la page)
    if (location.search !== newSearch) {
      history.replace({
        ...location,
        search: newSearch
      });
    }
  }, [history, location, queryParams, sourceInRedux]);

  /**
   * Synchronise visible with trackingMode
   */
  useEffect(() => {
    setVisible((v) => {
      if (trackingMode) lastVisible.current = v;
      return trackingMode ? false : lastVisible.current;
    });
  }, [trackingMode]);

  /**
   * User selected location update
   */
  useEffect(() => {
    const data = {
      departure: {
        lat: departureSelectedAddress?.location?.lat ?? 0,
        lon: departureSelectedAddress?.location?.lon ?? 0
      },
      arrival: {
        lat: arrivalSelectedAddress != null ? arrivalSelectedAddress.location.lat : 0,
        lon: arrivalSelectedAddress != null ? arrivalSelectedAddress.location.lon : 0
      }
    };
    setSelectedInputs(data);
  }, [arrivalSelectedAddress, departureSelectedAddress?.location?.lat, departureSelectedAddress?.location?.lon]);

  /**
   * Actions to do when half size changed
   */
  useEffect(() => {
    if (isMerge) {
      setPosition('12');
    } else {
      setPosition('71');
    }
  }, [isMerge]);

  /**
   * Clear all states when cancel one of searches (To Add each time we have things to refresh in future)
   */
  useEffect(() => {
    if (departureInput === '' || arrivalInput === '') {
      setITakeMyCar(false);
      setITakeMyCarData(null);
      setSelectedItinerary('');
      setItineraries([]);
      setJourneys([]);
      setVisible(false);
      console.log('here');
    }
  }, [departureInput, arrivalInput]);

  /**
   * Recalcul la dimension de la map en cas de changement de layout de la page, est-ce encore utile ???
   */
  useLayoutEffect(() => {
    setTimeout(() => {
      if (map !== undefined) map.resize();
    }, 100);
  }, [map]);

  /**
   * Return to user location function
   */
  const returnToUserLocation = useCallback(() => {
    if (map !== undefined && userLocation.latitude != null && userLocation.longitude != null) {
      map.flyTo({
        center: [userLocation.longitude, userLocation.latitude],
        zoom: 16,
        speed: 1.2 // vitesse de l'animation
      });
    }
  }, [map, userLocation.latitude, userLocation.longitude]);

  const resetPitch = useCallback(() => {
    map?.flyTo({
      pitch: 0,
      bearing: 0,
      duration: 500
    });
  }, [map]);

  /**
   * Remove expired routes history
   */
  useEffect(() => {
    // Remove expired entries
    const currentTime = Date.now();
    const expirationTime = currentTime - EXPIRATION_TIME;
    removeExpiredRoutes(expirationTime);
  }, [removeExpiredRoutes]);

  return (
    <IonPage>
      <IonContent>
        <div className="absolute left-0 right-0 bottom-0 top-0">
          <MapComponent showVehicle={animated} vehicleManager={vehicleManager}></MapComponent>
        </div>
        <div className="flex flex-col-reverse h-full w-screen items-center">
          {/* Floated Components */}
          {/* Header elements */}
          <Header isHome={true} />
          {userLocation.longitude != null && userLocation.latitude != null && (
            <>
              {/* User location input (Add possibility to edite current location) */}
              {!trackingMode && (
                <>
                  <DepartureInput
                    autoCompleteDepartureValues={autocompleteDepartureValues}
                    setAutocompleteDepartureValues={setAutocompleteDepartureValues}
                    lat={userLocation.latitude}
                    lon={userLocation.longitude}
                    departureSelectedAddress={departureSelectedAddress}
                    setDepartureSelectedAddress={setDepartureSelectedAddress}
                    setInput={setDepartureInput}
                    input={departureInput}
                    isMerge={isMerge}
                    setIsMerge={setIsMerge}
                    DepartureInputRef={DepartureInputRef}
                    setITakeMyCarData={setITakeMyCarData}
                    arrivalInput={arrivalInput}
                    setSelectedTrip={setSelectedTrip}
                    setTripListHeight={setTripListHeight}
                  />
                  {/* Arrival search field */}
                  <ArrivalInput
                    autoComplete={autocompleteValues}
                    lat={userLocation.latitude}
                    lon={userLocation.longitude}
                    setAutocompleteValues={setAutocompleteValues}
                    setArrivalSelectedAddress={setArrivalSelectedAddress}
                    setShowTripsCard={setVisible}
                    visible={visible}
                    position={position}
                    setPosition={setPosition}
                    date={date}
                    setDate={setDate}
                    setIsEditedDate={setIsEditedDate}
                    isEditedDate={isEditedDate}
                    setVisible={setVisible}
                    setSelectedTrip={setSelectedTrip}
                    setTripListHeight={setTripListHeight}
                    setAnimated={setAnimated}
                    departureSelectedAddress={departureSelectedAddress}
                    setInput={setArrivalInput}
                    input={arrivalInput}
                    setITakeMyCarData={setITakeMyCarData}
                    isMerge={isMerge}
                    setIsMerge={setIsMerge}
                    departureInput={departureInput}
                    departureInputRef={DepartureInputRef}
                    setDepartureInput={setDepartureInput}
                    setDepartureSelectedAddress={setDepartureSelectedAddress}
                    setAutocompleteDepartureValues={setAutocompleteDepartureValues}
                    setItineraries={setItineraries}
                    setSelectedItinerary={setSelectedItinerary}
                    setGoItineraries={setGoItineraries}
                    setBackupReturnItineraries={setBackupReturnItineraries}
                  />
                </>
              )}
              {/* Trips list */}
              <div id="tripList_id" className="flex items-center z-10 md:w-3/5 w-full justify-center">
                {/* FLoating bottom icons */}
                {/* End fLoating bottom icons */}
                <DetailModal
                  visible={visible}
                  setVisible={setVisible}
                  showArrowTop={showArrowTop}
                  setTripListHeight={setTripListHeight}
                  setShowArrowTop={setShowArrowTop}
                  setSelectedTrip={setSelectedTrip}
                  setAnimated={setAnimated}
                  ITakeMyCar={ITakeMyCar}
                  setITakeMyCar={setITakeMyCar}
                  setSelectedItinerary={setSelectedItinerary}
                  setIsMerge={setIsMerge}
                  setItineraries={setItineraries}
                  setGoItineraries={setGoItineraries}
                  setBackupReturnItineraries={setBackupReturnItineraries}
                  handleResetMap={handleResetMap}
                >
                  <div
                    style={{ padding: 15, maxHeight: `${tripListHeight}dvh`, overflow: 'hidden', overflowY: 'auto' }}
                    className="transition-top duration-500 ease"
                  >
                    <TripList
                      selectedAddress={arrivalSelectedAddress}
                      departure={{ lat: userLocation.latitude, lon: userLocation.longitude }}
                      date={date}
                      setShowArrowTop={setShowArrowTop}
                      setTripListHeight={setTripListHeight}
                      tripListHeight={tripListHeight}
                      selectedTrip={selectedTrip}
                      setSelectedTrip={setSelectedTrip}
                      isEditedDate={isEditedDate}
                      setPosition={setPosition}
                      departureSelectedAddress={departureSelectedAddress}
                      ITakeMyCar={ITakeMyCar}
                      setITakeMyCar={setITakeMyCar}
                      ITakeMyCarData={ITakeMyCarData}
                      setITakeMyCarData={setITakeMyCarData}
                      itineraries={itineraries}
                      setItineraries={setItineraries}
                      selectedItinerary={selectedItinerary}
                      setSelectedItinerary={setSelectedItinerary}
                      isMerge={isMerge}
                      setIsMerge={setIsMerge}
                      selectedInputs={selectedInputs}
                      setVisible={setVisible}
                      goItineraries={goItineraries}
                      setGoItineraries={setGoItineraries}
                      backupReturnItineraries={backupReturnItineraries}
                      setBackupReturnItineraries={setBackupReturnItineraries}
                      visible={visible}
                      journeys={journeys}
                      setJourneys={setJourneys}
                      selectedLeg={selectedLeg}
                      setSelectedLeg={setSelectedLeg}
                      isModalOpen={isModalOpen}
                    />
                  </div>
                </DetailModal>
              </div>
              <div className="flex flex-col z-10 self-end pb-10">
                <SettingsFloatingActions text="Settings" onClick={toggleModal} />
                <GpsFloatingActions returnToUserLocation={returnToUserLocation} />
                <NavigateFloatingActions
                  navigate={() => {
                    // Pour iOS 13+, il faut demander la permission
                    if (typeof DeviceOrientationEvent !== 'undefined' && typeof (DeviceOrientationEvent as any).requestPermission === 'function') {
                      (DeviceOrientationEvent as any)
                        .requestPermission()
                        .then((response: string) => {
                          if (response === 'granted') {
                            if (trackingMode) {
                              resetPitch();
                            }
                            switchTrackingMode();
                          }
                        })
                        .catch(console.error);
                    } else {
                      if (trackingMode) {
                        resetPitch();
                      }
                      switchTrackingMode();
                    }
                  }}
                ></NavigateFloatingActions>
              </div>
            </>
          )}
          {isModalOpen && (
            <Modal isOpen={isModalOpen} onClose={setIsModalOpen}>
              <InputSliders isBikeFactorValuesChanged={isBikeFactorValuesChanged} setIsBikeFactorValuesChanged={setIsBikeFactorValuesChanged} />
            </Modal>
          )}
        </div>
      </IonContent>
    </IonPage>
  );
};

export default Home;
